/**
 * Mirrors the response envelopes emitted by `backend/app/src/core/base`.
 *
 * - Single-item: `{ data: T }`
 * - Paginated:   `{ data: T[], page: { page, page_size, total, total_pages } }`
 */

export interface OneEnvelope<T> {
  data: T;
}

/**
 * Page metadata. Field naming follows the backend's wire format
 * (`page_size`, `total_pages`); don't camelCase here or reads break.
 */
export interface PageMeta {
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
}

export interface ManyEnvelope<T> {
  data: T[];
  page: PageMeta;
}

export interface ListQuery {
  page?: number;
  pageSize?: number;
  sort?: string;
  /** Each value can be a primitive or an array (for `multi: true` filters). */
  filter?: Record<string, string | number | Array<string | number> | undefined>;
  include?: string[];
}

/** Builds an axios `params` object from a typed ListQuery. */
export function listQueryToParams(q: ListQuery | undefined): Record<string, unknown> {
  if (!q) return {};
  const params: Record<string, unknown> = {};
  if (q.page !== undefined) params.page = q.page;
  // Backend expects snake_case `page_size` (see core/query/query-options.ts).
  if (q.pageSize !== undefined) params.page_size = q.pageSize;
  if (q.sort) params.sort = q.sort;
  if (q.include?.length) params.include = q.include.join(',');
  if (q.filter) {
    for (const [key, value] of Object.entries(q.filter)) {
      if (value === undefined || value === null) continue;
      // Backend `multi: true` filters expect a single comma-separated
      // string (`filter[key]=1,2,3`). Sending an array would make axios
      // serialize as `filter[key][0]=…&filter[key][1]=…`, which the query
      // parser rejects as "must be a string value". Join here so the
      // multi-select UIs in the app actually work.
      params[`filter[${key}]`] = Array.isArray(value) ? value.join(',') : value;
    }
  }
  return params;
}
